import React, {useEffect, useRef, useMemo, useState} from 'react'
import _ from 'lodash/fp'
import ClipboardJS from 'clipboard'
import {Dialog, ContextMenu, Li, Button, Icon, willUseFlipSwitch, Loading, TransitionDiv} from '@startlibs/components'
import {
  ToggleCheckbox,
  WithForm,
  AutoComplete,
  useFormValue,
  Errors,
  FormValue,
  Field,
  TextInput,
  SimpleCheckbox,
  validation,
  DatePicker 
} from '@startlibs/form'
import {useLazyConstant, usePopupToggle, useToggle} from '@startlibs/core'
import {LoadingErrorDialog} from './LoadingErrorDialog'
import styled, {css} from 'styled-components'
import {callIfFunction, getColor} from '@startlibs/utils'
import {twoWayAnimation, media, formFetch, postFetcher} from '@startlibs/utils'
import {useNotificationRef} from '../components/Notifications'
import {getStudyDescription} from '../utils'
import { getFetcherWithBearer, postFetcherWithBearer } from '../utils/authFetch'
import { darken } from 'polished';

const [onEnter, onLeave] = twoWayAnimation('opacity:0; transform:translateY(50%)', 'opacity:1;transform:translateY(0)')
const [loadingEnter, loadingLeave] = twoWayAnimation('opacity:0;', 'opacity:1;')


const SharingDialog = styled(Dialog)`
  .suggested-emails {
    margin-top: -0.5rem;
    margin-bottom: 1.25rem;
    font-weight: 600;
    a {
      text-decoration: underline;
      font-weight: 400;
      color: ${getColor('main')};
    }
  }
  ${media.max(430)`
    ${Button} {
      min-width: unset;
      padding-left: 1rem;
      padding-right: 1rem;
      & ~ ${Button} {
        margin-left: 0.75rem;
      }
    }
  `}
  ${media.max(390)`
    padding: 1rem;
    ${Button} {
      padding-left: 0.75rem;
      padding-right: 0.75rem;
      ${Icon} {
        display: none;
      }
    }
  `}
`

const AdditionalText = styled.p`
  margin-top: .5rem;
`

const EXPIRATION_TIMES = [0, 48, 168, -1]

const EXPIRATION_LABELS = {
  [0]: 'Never (access link will not expire)',
  [48]: '48 hours (recipients will not have access after 48 hours)',
  [168]: '1 week (recipients will not have access after 1 week)',
  [-1]: 'Custom (select expiration date)'
}

export const ExamIdentification = styled.div`
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.25);
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  .study-info {
    color: rgba(0,0,0,0.7);
    margin-top: 2px;
    :first-child {
      font-size: 14px;
      font-weight: 600;
      color: inherit;
      margin-top: 0;
    }
  }
  ${ToggleCheckbox} {
    margin: 0.75rem -1rem -0.75rem;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }
`

const OtherStudiesContainer = styled.div`
  margin-bottom: 1.5rem;
  border-radius: 6px;
  overflow: hidden;
  ${props => !props.emptyList && `
    border: 1px solid rgba(0,0,0,0.25);
  `}
`

const AddOtherStudiesDiv = styled.div`
  background-color: rgba(0,0,0,0.06);
  padding: 0.5rem 1rem;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  font-size: 13px;
  ${props => props.clickable && `
    cursor: pointer;
    :hover {
      background-color: rgba(0,0,0,0.08);
    }
    :active {
      background-color: rgba(0,0,0,0.1);
    }
  `}
  
  a {
    font-weight: 600;
    color: ${getColor('main')};
  }
`

const AddStudiesSubtitleDiv = styled.div`
  font-size: 13px;
  display: flex;
  padding: 0.5rem 0rem;
  margin-bottom: 0.5rem;
  justify-content: space-between;
  color: #5A5A5A;
  font-weight: 600;
  margin-top: -6px;
  a{
    color: #1888CB;
  }
  a:hover{
    opacity: 0.8;
  }
  b{
    font-weight: 900;
  }
`

const FilteringDiv = styled.div`
  display: flex;
  margin-bottom: 1rem;
  align-items: center;
  font-size: 13px;
  padding: 0.75rem 1rem;
  border-radius: 5px;
  border: 1px solid ${getColor('gray210')};
  ${Icon} {
    margin-left: 0.25rem;
    font-size: 14px;
    vertical-align: -2px;
    cursor: pointer;
  }
`

const ToggleCheckboxComponent = styled(ToggleCheckbox)`
  font-weight: 600;
  ${props => props.disabled && `
    opacity: 0.8;
    pointer-events:none;
  `}
`
const EmailConfirmationListContainer = styled.div`
  background-color: rgba(0,0,0,.07);
  border: 1px solid rgba(0,0,0,.1);
  border-radius: 5px;
  margin-bottom: 1rem;
  font-size: 13px;
  padding: 1rem;
  p {
    margin-bottom: 0;
  }
  ul {
    list-style-type: disc;
    margin-top: .5rem;
    margin-left: 2rem;
  }
`
const SecurityDataWarning = styled.div`
  color: rgba(0,0,0,0.5);
  h4 {
    font-size: 13px;
    margin-bottom: 0.25rem;
  }
  p {
    font-size: 12px;
    margin-bottom: 0;
  }
`
const FieldDescription = styled.div`
  color: rgba(0,0,0,0.5);
  font-size: 12px;
  margin-top: -0.5rem;
`

const StyledRow = styled.div`
  cursor: pointer;
  &:nth-child(odd) {
    background-color: #EFF4F7;
  }
  &:hover {
    background: rgba(0,139,210,0.15);
  }
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  // justify-content: flex-start;
  align-items: center;
  border-bottom: 1px solid ${getColor('gray210')};
  padding: 1rem;
  &:last-child {
    border-bottom: 0px;
  }
  .info{
    width: 400px;
  }
  .info-box {
    margin-top: 2px;
    font-size: 12px;
  }
  ${props => props.selected && css`
    &:nth-child(odd), &:nth-child(even) {
      background-color: ${getColor('main')};
      color: white;
      &:hover {
        background: ${getColor('main')};
      }
    }
  `}
  label {
    margin-bottom: 0;
  }
`

const ListArea = styled.div`
  border: 1px solid ${getColor('gray210')};
  border-radius: 6px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  margin-bottom: 1rem;
  ::-webkit-scrollbar {
    width: 16px;
    height: 16px;
    background: rgba(0,0,0,0.05);
    border-radius: 0 5px 5px 0;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(0,0,0,0.25);
    border-radius: 100px;
    background-clip: padding-box;
    border: 4px solid transparent;
    min-height: 30px;
    min-width: 30px;
    &:hover {
      background: rgba(0,0,0,0.35);
      background-clip: padding-box;
      border: 4px solid transparent;
    }
  }
`

const OtherStudiesRow = styled.div`
  a {
    color: ${getColor('main')};
    cursor: pointer;
    text-decoration: underline;
    :hover {
      color: ${props => (darken(0.075, getColor('main')(props)))};
    }
  }
  & ~ & {
    margin-top: 0.25rem;
  }
`

const OtherStudiesList = styled.div`
  padding: 0.75rem 1rem;
`

const PatientSeparator = styled.div`
  border-top: 1px solid ${getColor('gray210')};
  border-bottom: 1px solid ${getColor('gray210')};
  background: ${getColor('gray240')};
  height: 1.25rem;
`

const InvisibleSeparator = styled.hr`
  margin: 0; 
  height: 0px;
  background: white; 
  border: none;
`

const PatientIdParagraph = styled.div`
  border-bottom: 1px solid ${getColor('gray210')};
  padding: 0.75rem 1rem;
  font-size: 13px;
`

const validateEmail = (email) => {
  const re = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
  return re.test(email)
}
const emailValidation = (v = '') => validateEmail(v.trim().replace(/\u200b/g, '')) ? {success: v.trim().replace(/\u200b/g, '')} : {errors: [`"${v}" is not a valid e-mail address.`]}

const preValidation = ({emails}) => {
  const hasEmails = emails.find(e => !!e)
  const invalidEmails = emails.filter(e => e && !validateEmail(e))
  if (!hasEmails)
    return {emails: [`At least one valid e-mail address is needed.`]}
  if (invalidEmails.length) {
    return {}
  }
}
// virtualbox test
// const SPA_ORIGIN = location.hostname === "10.0.2.2" ? "" :'https://share.purview.net'

// Prod
export const SPA_ORIGIN = () => window.SHARE_ORIGIN || 'https://share.purview.net'
export const WORKLIST_ORIGIN = () => window.WORKLIST_ORIGIN || 'https://purviewimage-us-east1.purview.net'

// Dev Ohio
// export const SPA_ORIGIN = () => window.SHARE_ORIGIN || '' // 'https://shareservice-us-east-2.purview.net' 
// export const WORKLIST_ORIGIN = () => window.WORKLIST_ORIGIN || "" // ||'https://purviewimage-us-east-2.purview.net'
export const AUTHORIZE = (jwt, origin = SPA_ORIGIN()) => postFetcher(origin + '/api/system/authenticate?t=' + jwt)
export const GET_LINK = (jwt, params, origin = SPA_ORIGIN()) => postFetcher(origin + '/api/share/link?t=' + jwt, params)

export const ExamCard = ({study, patientName}) => <ExamIdentification>
  {patientName && <div className="study-info notranslate">{patientName}</div>}
  <div className="study-info notranslate">{getStudyDescription(study)}</div>
  <div className="study-info notranslate">Referring Physician: {study.referringPhysician}</div>
</ExamIdentification>

const defaultInfoCard = ({patientName,study, otherStudies}) => <>
  {patientName && <div className="study-info notranslate">{patientName}</div>}
  <div className="study-info notranslate">{getStudyDescription(study)}</div>
  <div className="study-info notranslate">Referring Physician: {study.referringPhysician}</div>
  {otherStudies && otherStudies.length > 0 && <div className="study-info">{
    otherStudies
      .find(s => s.studyIuid === study.studyUID).tagsStudy
        .sort((a, b) => a.tags.description > b.tags.description ? 1 : -1 )
        .map((tag, i)  => <Tag className="notranslate" title={tag.tags.description} key={tag.tags.idTag}>{tag.tags.description}</Tag>)}
  </div>}
</>


// const defaultInfoCard = (patientName,study,authorization) => <>
//   {patientName && <div className="study-info">{patientName}</div>}
//   <div className="study-info">{getStudyDescription(study)}</div>
//   <div className="study-info">Referring Physician: {study.referringPhysician}</div>
//   <p>Test: {authorization && authorization.isOpen && authorization.isOpen.otherStudies && authorization.isOpen.otherStudies.length}</p>
// </>


const DEFAULT_FORM_VALUES = ({providerInfo,shareDefaults,hasReport, caseRequest}) => {
  const shareStudyReport = providerInfo.sharingOptions.includeStudyReport === 2 && hasReport
  let values = {emails: [], ...providerInfo.sharingOptions, shareStudyReport}
  if (!providerInfo.phiProtectionImplemented) {
    values = {...values,sendSecurely:false}
  }
  if (caseRequest) {
    values = {...values,...shareDefaults}
  }
  if (providerInfo.sharingOptions.viewerDisabled) {
    values = {...values,allowImageDownload:true}
  }
  return values
}

const Tag = styled(({title, ...props}) => <div  {...props}/>)`
  border-radius: 100px;
  background: rgba(185, 200, 210, 0.3);
  border: 1px solid rgba(185, 200, 210, 0.6);
  display: inline-block;
  padding: 0 0.5rem;
  margin-right: 0.25rem;
  margin-left: 0.25rem;
  font-size: 11px;
  line-height: 14px;
  margin-top: 3px;
  color: rgba(0,0,0,0.75);
  max-width: 122px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: -5px;  
  ${props => props.clickable && `
    cursor: pointer;
    :hover, :active {
      background: rgba(185, 200, 210, 0.6);
      border: 1px solid rgba(185, 200, 210, 0.9);
    }
  `}
`

const preValidationSecond = validation.buildValidation({
  subject: [validation.required]
})

export const ShareDialog = (
  {
    closeDialog, caseRequest, caseRequestInfo, notifyAndClose, jwt, suggestedEmails = [], labels = {},
    shareDefaults = {allowSubmit: false, shareCase: true},
    shareableLinkIsOpen, authorize = AUTHORIZE, 
    infoCard = caseRequestInfo || defaultInfoCard, 
    defaultFormValues = DEFAULT_FORM_VALUES, transform: rawTransform, getLink=GET_LINK, authorizationIsOpen,
    shareFetch,
    ...rest
  }) => {

  const error = useToggle()
  const authorization = useToggle(authorizationIsOpen)
  const formRef = useRef()

  

  useEffect(() => {
    if (jwt) {
      authorize(jwt)
        .then((auth) => {
          
          const worklistApiServiceToken = auth.serviceToken || ""
          if(worklistApiServiceToken != ""){
            // getFetcherWithBearer(worklistApiServiceToken)(WORKLIST_ORIGIN()+`/worklist/api/studies-multishare?studyIuid=${auth.study.studyUID}`)
            postFetcherWithBearer(worklistApiServiceToken)(WORKLIST_ORIGIN()+`/worklist/api/studies-multishare?studyIuid=${auth.study.studyUID}`,{
                attrs: [
                  // "patId",
                  "patName",
                  "patBirthdate",
                  "patSex"
                ],
                studyIUID: auth.study.studyUID
              })
              .then(studies => {
                // authorization.openWith({...auth, otherStudies: studies.filter(study => study.studyIuid !== auth.study.studyUID)})
                authorization.openWith({...auth, otherStudies: studies})
              })
              .catch((err)=>{
                console.log(err)
                authorization.openWith(auth)    
              })
          }else{
            authorization.openWith(auth)
          }          
          // authorization.openWith({...auth, otherStudies: otherStudiesData})
          // authorization.openWith(auth)
        })
        .catch(error.open)
    }
  }, [jwt])

  const [, setNotification] = useNotificationRef()
  const closeWithNotification = (notification, notificationType) => {
    if (notifyAndClose) {
      notifyAndClose(notification, notificationType)
    } else {
      setNotification(notification)
      closeDialog()
    }
  }


  const transform = rawTransform || (({emails, message, subject, ...sharingOptions}) => { 

    // order by studyDate so, when open the share, it will display most recent on top
    let shareStudies = selectedStudies
    // if every study from selectedStudies has studyDate, order by studyDate
    if(shareStudies.length > 0 && shareStudies.every(study => study.studyDatetime)){
      shareStudies.sort((a, b) => new Date(b.studyDatetime) - new Date(a.studyDatetime))
    }

    if(sharingOptions.expirationTime === -1 && sharingOptions.expirationDate){
      let expirationDate = sharingOptions.expirationDate
      let now = new Date()
      let expirationTime = new Date(expirationDate).getTime() - now.getTime()
      // add 24 to consider the entire day. Add 1 plus hors to account for passed minutes.
      let expirationTimeIntHours = Math.floor(expirationTime / 1000 / 60 / 60) + 24 + 1
      sharingOptions.expirationTime = expirationTimeIntHours
    }
    
    return caseRequest
    ? ({
      mailsTo: _.filter(email => email, emails),
      sharingOptions,
      studyUID: caseRequest.requestCode,
      message: message && message.length > 0 ? message : null,
      additionalStudies: shareStudies.map(study => {
        return {
          studyUID: study.studyIuid,
          patientId: study.patId,
          studyDescription: study.studyDesc || "",
          modalities: study.modsInStudy || "",
          studyDate: study.studyDatetime && study.studyDatetime.slice(0,10) || "",
          images: study.numInstances,
          referringPhysician: study.refPhysician || "",
          institution: study.institution
        }  
      }),


    })
    : ({
      mailsTo: _.filter(email => email, emails),
      sharingOptions,
      studyUID: authorization.get().study.studyUID,
      message: message && message.length > 0 ? message : null,
      customEmailSubject: subject && subject.trim().length > 0 ? subject : null,
      additionalStudies: shareStudies.map(study => {
        return {
          studyUID: study.studyIuid,
          patientId: study.patId,
          studyDescription: study.studyDesc || "",
          modalities: study.modsInStudy || "",
          studyDate: study.studyDatetime && study.studyDatetime.slice(0,10) || "",
          images: study.numInstances,
          referringPhysician: study.refPhysician || "",
          institution: study.institution
        }  
      }),
    })
  }
    
  )

  /* Hook rules, can't have hooks after if, this return is just a regular function call, so rules apply */
  const confirmDialog = useToggle()
  const shareableLink = useToggle(shareableLinkIsOpen)
  const shareLink = useToggle()
  const [selectedStudies, setSelectedStudies] = useState([])
  const [dialogSelectedStudies, setDialogSelectedStudies] = useState([])

  const formAction = confirmDialog.isOpen ? (shareFetch || formFetch(SPA_ORIGIN() + '/api/share?t=' + authorization.isOpen.jwt)) : confirmDialog.open

  const flipAnimation = useLazyConstant(willUseFlipSwitch)

  /**
   * Performs pre-validation of form data.
   *
   * It checks for:
   * - at least one valid email address;
   * - valid email addresses;
   * - if expiration time is custom, expiration date is required;
   * - if confirm dialog is open, subject is required.
   *
   * @param {object} data - form data.
   * @param {string[]} data.emails - list of email addresses.
   * @param {string} data.subject - email subject.
   * @param {object} data.sharingOptions - sharing options.
   * @param {number} data.sharingOptions.expirationTime - expiration time in hours.
   * @param {string} [data.sharingOptions.expirationDate] - expiration date.
   *
   * @returns {object} Object with error messages or undefined.
   */
  const preValidation = ({ emails, subject, ...sharingOptions }) => {
    const isCustomExpiry = sharingOptions.expirationTime === -1;
    const hasCustomExpiryDate = Boolean(sharingOptions.expirationDate);
    const provideExpiryDate = isCustomExpiry && !hasCustomExpiryDate;
    
    const now = new Date()
    // Need to add 24 hours to the expiration date, because the datavalue uses 00:00:01 of the day.
    // Need to add another hour to account for sometime at 23 and minutes...
    const expirationDatePlus25 =  hasCustomExpiryDate && new Date(sharingOptions.expirationDate).getTime() + (25 * 60 * 60 * 1000)
    const expirationDateIsPast = isCustomExpiry && expirationDatePlus25 && expirationDatePlus25 < now
    const hasEmails = emails.find(e => !!e)
    const invalidEmails = emails.filter(email => email && !validateEmail(email));
  
    // If invalid emails exist, return an empty object to prevent submission but show no errors
    if (invalidEmails.length > 0) return {};
  
    // Collect validation errors
    const errors = [
      !hasEmails && { emails: ['At least one valid e-mail address is needed.'] },
      provideExpiryDate && { expirationDate: ['Expiration date is required.'] },
      confirmDialog.isOpen && (!subject?.trim()) && { subject: ['Subject is required.'] },
      expirationDateIsPast && { expirationDate: ['Expiration date is in the past'] }
    ]
      .filter(Boolean) // Remove falsy values
      .reduce((acc, err) => ({ ...acc, ...err }), {});
  
    return Object.keys(errors).length ? errors : undefined;
  };
  

  if (error.isOpen) {
    return <LoadingErrorDialog errorCode="02" closeDialog={closeDialog}/>
  }

  return <WithForm
    alwaysSave
    skipDom
    ref={formRef}
    action={formAction}
    transform={transform}
    onSuccess={() => {
      if (confirmDialog.isOpen) {
        closeWithNotification('Records successfully shared via email', 'email')
      }
    }}
    preValidation={preValidation}
  >{form =>
    authorization.isOpen
      ? AuthorizedShareDialog({
        closeDialog,
        closeWithNotification,
        authorization: authorization.isOpen,
        confirmDialog,
        shareableLink,
        shareLink,
        transform,
        flipAnimation,
        defaultFormValues,
        suggestedEmails,
        caseRequest,
        infoCard,
        form,
        getLink,
        shareDefaults,
        labels,
        selectedStudies, 
        setSelectedStudies,
        dialogSelectedStudies,
        setDialogSelectedStudies,
        ...rest
      })
      : LoadingDialog({closeDialog, form, flipAnimation, caseRequest, labels, ...rest})
  }</WithForm>
}

const LoadingDialog = ({closeDialog, form, flipAnimation, caseRequest, labels}) => <SharingDialog
  closeDialog={closeDialog} withFormWrap
  title={labels.firstStepTitle || (caseRequest ? "Share expert view" : "Share medical record")}>
  <FlipDiv
    key="loading"
    css="padding:12rem 0"
    onEnter={loadingEnter}
    onLeave={loadingLeave}
    useAnimation={flipAnimation}
  >
    <Loading autoFocus absolute size={40} borderWidth={6}/>
  </FlipDiv>
</SharingDialog>


export const AuthorizedShareDialog = ({
                                        closeDialog, closeWithNotification, authorization, confirmDialog, shareableLink, shareLink, form, transform, caseRequest, infoCard, flipAnimation, labels,
  defaultFormValues,getLink,
  selectedStudies, setSelectedStudies,
  dialogSelectedStudies, setDialogSelectedStudies,
                                        ...rest}) => {
  const {providerInfo, jwt, study, patientName, hasReport, patientDob, patientSex} = authorization

  const computedInfoCard = callIfFunction(infoCard,authorization)
  // const [selectedStudies, setSelectedStudies] = useState([])

  return !confirmDialog.isOpen
    ? <SharingDialog
      className="JS-copy-focus-container notranslate"
      withFormWrap
      closeDialog={closeDialog}
      title={callIfFunction(labels.firstStepTitle,shareableLink.isOpen) || (caseRequest ? "Share expert view" : "Share medical record")}
      auxiliarActions={
        shareableLink.isOpen
          ? <Button onClick={shareableLink.close} icon="email">Send via email</Button>
          : <Button
              // onClick={shareableLink.open} 
              onClick={() => {
                if ((form.getValue('expirationTime') === -1) && (!form.getValue('expirationDate'))) {
                  form.setError('expirationDate', 'Expiration date is required');
                  return
                }
                // Need to add 24 hours to the expiration date, because the datavalue uses 00:00:01 of the day.
                // Need to add another hour to account for sometime at 23 and minutes...
                let now = new Date()
                let expirationDatePlus25 = new Date(form.getValue('expirationDate')).getTime() + (25 * 60 * 60 * 1000)

                if((form.getValue('expirationTime') === -1) && (expirationDatePlus25 < now) ) {
                  form.setError('expirationDate', 'Expiration date is in the past');
                  return
                }
                shareableLink.open()
              }}
              icon="link">Get shareable link</Button>
      }
      footer={
        shareableLink.isOpen ? <>
          {closeDialog && ClipboardJS.isSupported() && <Button onClick={closeDialog}>Cancel</Button>}
          <CopyButton url={shareLink.isOpen} highlight onCopy={(supported) => closeWithNotification(supported ? 'Shareable link copied to clipboard' : "You've finished the share function")}/>
        </> : <>
          {closeDialog && <Button onClick={closeDialog}>Cancel</Button>}
          <Button highlight type="submit" onClick={form.willSubmitForm}>Send email</Button>
        </>
      }
    >
      <FlipDiv
        key="share"
        onEnter={loadingEnter}
        onLeave={loadingLeave}
        useAnimation={flipAnimation}
        className="notranslate"
      >
        <FirstStep
          closeDialog={closeDialog}
          caseRequest={caseRequest}
          providerInfo={providerInfo}
          study={study}
          infoCard={computedInfoCard}
          hasReport={hasReport}
          jwt={jwt}
          authorization={authorization}
          transform={transform}
          closeWithNotification={closeWithNotification}
          shareableLink={shareableLink}
          shareLink={shareLink}
          form={form}
          getLink={getLink}
          labels={labels}
          defaultFormValues={defaultFormValues}
          action={confirmDialog.openWith}
          selectedStudies={selectedStudies}
          setSelectedStudies={setSelectedStudies}
          dialogSelectedStudies={dialogSelectedStudies}
          setDialogSelectedStudies={setDialogSelectedStudies}
          patientName={patientName}
          patientDob={patientDob}
          patientSex={patientSex}
          {...rest}
        />
      </FlipDiv>
    </SharingDialog>
    : <SecondStepDialog
      form={form}
      labels={labels}
      closeWithNotification={closeWithNotification}
      closeDialog={closeDialog}
      infoCard={computedInfoCard}
      caseRequest={caseRequest}
      confirmDialog={confirmDialog}
      authorization={authorization}
      selectedStudies={selectedStudies}
      setSelectedStudies={setSelectedStudies}
      className="notranslate"
    />
}


const FirstStep = ({
                     closeDialog, action, form, jwt, providerInfo, authorization, closeWithNotification, infoCard, hasReport, shareableLink, shareLink, caseRequest, transform, suggestedEmails, suggestionLabel, shareDefaults,
  defaultFormValues, getLink, labels
  ,selectedStudies, setSelectedStudies,
  dialogSelectedStudies, setDialogSelectedStudies, patientName, patientSex, patientDob
}) => {

  const selectingOtherStudies = useToggle()
  // const [selectedStudies, setSelectedStudies] = useState([])
  const linkFetch = useMemo(() => (params) => getLink(jwt, params), [jwt])

  const loadingShareableLink = useToggle(false)
  const autocompleteRef = useRef()

  const flipAnimation = useLazyConstant(willUseFlipSwitch)
  const [activeFilteringTags, setActiveFilteringTags] = useState([])
  const mainStudyPatientId =  authorization && authorization.otherStudies && authorization.otherStudies.filter(study => study.studyIuid === authorization.study.studyUID)[0].patId
  const availableStudiesCount = authorization && authorization.otherStudies && authorization.otherStudies.filter(study => study.studyIuid !== authorization.study.studyUID).length
  const filteredOtherStudies = authorization.otherStudies
    .filter(study => {
      if (activeFilteringTags.length === 0) return true
      return study.tagsStudy.some(tag => activeFilteringTags.some(tf => tf.tags.idTag === tag.tags.idTag))
      // return activeFilteringTags.every(tf => 
      //   study.tagsStudy.some(tag => tf.tags.idTag === tag.tags.idTag));
    })
    .filter(study => study.studyIuid !== authorization.study.studyUID)
  const otherStudiesSameId = authorization && authorization.otherStudies && filteredOtherStudies && filteredOtherStudies.filter(study => study.patId === mainStudyPatientId)
  const otherStudiesDifferentId = authorization && authorization.otherStudies && filteredOtherStudies && filteredOtherStudies.filter(study => study.patId !== mainStudyPatientId)

  const groupByPatId = (items) => {
    // Initialize an empty object to hold the groups
    const groups = {};
  
    // Iterate over each item in the sorted array
    items.forEach(item => {
      // If a group for the item's patId doesn't exist, create it
      if (!groups[item.patId]) {
        groups[item.patId] = [];
      }
      
      // Add the item to the appropriate group
      groups[item.patId].push(item);
    });
  
    return groups;
  };

  // Group items by patId
  const groupedItems = groupByPatId(otherStudiesDifferentId);
 
  // sort groupedItems by patId
  const sortedGroupedItems = Object.keys(groupedItems)
    .sort((a, b) => a.localeCompare(b))
    .reduce((acc, key) => {
      acc[key] = groupedItems[key];
      return acc;
    }, {});

  useEffect(() => {
    if (!form.getValues('emails')) {
      form.setValues(callIfFunction(defaultFormValues,{providerInfo,hasReport,caseRequest,shareDefaults}),false)
    }
  }, [])

  useEffect(() => {
    
    if (form.getValues('expirationTime') === -1) {
      if (!form.getValues('expirationDate') || form.getValues('expirationDate') === '' || form.getValues('expirationDate') === null) {
        form.setError('expirationDate', 'Expiration date is required' );
        shareableLink.close();
        loadingShareableLink.close();
        return;
      }
    }
  
    updateShareableLink();
  }, [shareableLink.isOpen]);

  const updateShareableLink = () => {
    
    if (form.getValues('expirationTime') === -1) {
      if (!form.getValues('expirationDate') || form.getValues('expirationDate') === '' || form.getValues('expirationDate') === null) {
        form.setError('expirationDate',  'Expiration date is required' );
        shareableLink.close();
        loadingShareableLink.close();
        return;
      }
    }
  
    form.clearErrors();
    shareLink.close();
  
    if (shareableLink.get()) {
      form.setValue('emails', (emails) => emails ? emails.filter(validateEmail) : undefined);
      loadingShareableLink.wait(
        linkFetch(transform(form.getValues()))
          .then(({ url }) => {
            shareLink.openWith(url);
          })
          .catch(() => {
            console.log('Error fetching shareable link');
            form.setError('shareLink', 'Link failed to load' );
            shareLink.close();
          })
      );
    } else {
      if (form.getValues('expirationTime') > -1) {
        loadingShareableLink.open();
      }
    }
  };

  const addEmail = (email) => {
    autocompleteRef.current.addValue(email)
  }

  const filteredSuggestedEmails = suggestedEmails.filter(email => (form.getValue("emails") || []).indexOf(email) < 0)

  return <>
    <p>{labels.firstStepSubTitle}</p>
    <ExamIdentification>
      {infoCard}
      {
      !caseRequest && hasReport && !!providerInfo.sharingOptions.includeStudyReport &&
        <ToggleCheckboxComponent
          onChange={updateShareableLink}
          path="shareStudyReport"
          label="Include study report"
        />
     }
    </ExamIdentification>
    {/* {authorization && authorization.otherStudies && authorization.otherStudies.length > 0 && <OtherStudiesContainer emptyList={selectedStudies.length === 0}> */}
    {availableStudiesCount > 0 && <OtherStudiesContainer emptyList={selectedStudies.length === 0}>
      <AddOtherStudiesDiv clickable={true} onClick={() => {
          setDialogSelectedStudies(selectedStudies)
          if(shareableLink.isOpen){
            shareableLink.close()
          }
          selectingOtherStudies.open()
        }}
        
        >
        {selectedStudies.length === 0 
          ? <div><b>Add other studies from this patient</b> <span css="opacity:0.5;">({availableStudiesCount} available)</span></div>
          : <div><b>Add other studies from this patient</b> <span css="opacity:0.5;">({selectedStudies.length + "/" + availableStudiesCount} selected)</span></div>
        }
        <a>Add</a>
      </AddOtherStudiesDiv>
      {selectedStudies.length > 0 && <OtherStudiesList>
        {selectedStudies.map((study, i) => 
          <OtherStudiesRow key={study.studyIuid}>
            <span css="margin-right:0.5rem;" className="notranslate">{study.modsInStudy ?  study.modsInStudy + ' - ' : ''}{study.studyDesc && study.studyDesc.replace(/\^/g, ' ').trim()}{study.studyDatetime && " ("+study.studyDatetime.slice(0,10)+")"} {study.numInstances && study.numInstances > 0 && ` - ${study.numInstances} images`}
            {study.tagsStudy.sort((a, b) => a.tags.description > b.tags.description ? 1 : -1 )
                      .map((tag, i)  => <Tag className="notranslate" title={tag.tags.description} key={tag.tags.idTag}>{tag.tags.description}</Tag>)}
            </span>
            <a onClick={() => {
              setSelectedStudies(selectedStudies.filter(s => s.studyIuid !== study.studyIuid))
            }}>Remove</a>
          </OtherStudiesRow>)
        }  
      </OtherStudiesList>}
    </OtherStudiesContainer>}
    {
      providerInfo &&
      <Field label="Security Options">
        <ToggleCheckboxComponent disabled={!providerInfo.phiProtectionImplemented} onChange={updateShareableLink}
                                 path="sendSecurely" label="Send securely"
                                 descText={!providerInfo.phiProtectionImplemented
                                   ? 'Medical records may not be shared securely when either the patient date of birth or patient name is not available.'
                                   : (providerInfo.phiProtectionAttributes.dob && providerInfo.phiProtectionAttributes.lastName)
                                     ? 'Requires the recipient to know the patient’s date of birth and last name.'
                                     : providerInfo.phiProtectionAttributes.dob
                                       ? 'Requires the recipient to know the patient’s date of birth.'
                                       : 'Requires the recipient to know the patient’s last name.'}/>
        {
          !caseRequest && !providerInfo.sharingOptions.viewerDisabled &&
          <ToggleCheckboxComponent onChange={updateShareableLink} path="allowImageDownload"
                                   label="Allow recipients to download images"/>
        }
        {/*{
        caseRequestInfo &&
        <ToggleCheckboxComponent onChange={updateShareableLink} path="shareCase"
                                 label="Share case"/>
      }
      {
        caseRequestInfo &&
        <ToggleCheckboxComponent onChange={updateShareableLink} path="allowSubmit"
                                 label="Allow submit"/>
      }*/}
        <ExpirationSelect onChange={updateShareableLink}/>
      </Field>
    }

    <FlipDiv
      key={shareableLink.isOpen}
      useAnimation={flipAnimation}
    >
      {
        shareableLink.isOpen
          ? <CopyLinkComponent label="Copy and send the link below to share your record" url={shareLink.isOpen}
                               key={shareLink.isOpen} loadingShareableLink={loadingShareableLink.isOpen}/>
          : <div className='notranslate'>
            <AutoComplete
              ref={autocompleteRef}
              form={form}
              getLabel={_.identity}
              minQueryLength={1}
              confirmIfNewKeys={[' ']}
              defaultValue={[]}
              autoSelect
              validation={emailValidation}
              path="emails"
              label="Enter the recipients email address"
              placeholder="example@email.com"
            />
            {
              filteredSuggestedEmails.length > 0 &&
              <div className="suggested-emails"><span>{suggestionLabel || "Suggestions: "}</span>
                {filteredSuggestedEmails.map((email, i) =>
                  <span key={email}>
                        <a onClick={() => addEmail(email)}>{email}</a>
                    {i + 1 === filteredSuggestedEmails.length ? '' : ', '}
                  </span>
                )}
              </div>
            }
            <FieldDescription>Separate multiple email addresses using comma or space.</FieldDescription>
          </div>
      }
    </FlipDiv>
    <AdditionalText>{labels.additionalInfo}</AdditionalText>
    <Errors/>
    {selectingOtherStudies.isOpen && <Dialog 
      closeDialog={() => {
        // setSelectedStudies([])
        selectingOtherStudies.close()
        setActiveFilteringTags([])
      }}
      // onClose={selectingOtherStudies.close} 
      title="Add studies"
      footer={<>
        <Button onClick={() => {
          // setSelectedStudies([])
          selectingOtherStudies.close()
        }}>Cancel</Button>
        <Button 
          autoFocus 
          highlight 
          // isLoading={form.isLoading} 
          // type="submit" 
          // onClick={form.willSubmitForm}
          disabled={dialogSelectedStudies && dialogSelectedStudies.length === 0}
          onClick={() => {
            setSelectedStudies(dialogSelectedStudies)
            selectingOtherStudies.close()
          }}
        >
          Add {dialogSelectedStudies.length > 0 ? dialogSelectedStudies.length+' ' : ' '}selected stud{dialogSelectedStudies.length === 1 ? 'y' : 'ies'}
        </Button>
      </>}>
      {activeFilteringTags && activeFilteringTags.length > 0 && <FilteringDiv>
          <b>Filtering: </b>
          {activeFilteringTags.map((tag, i) => (
            <Tag className="notranslate" css="padding:0.25rem 0.75rem;margin-left:0.75rem;margin-top:0;" title={tag.tags.description} key={tag.tags.idTag}>{tag.tags.description} <Icon icon="x" onClick={() => 
              setActiveFilteringTags(activeFilteringTags.filter(t => t !== tag))
            }/></Tag>
          ))}
      </FilteringDiv>}
      <AddStudiesSubtitleDiv>
        {otherStudiesSameId && otherStudiesSameId.length > 0 ?
          <span>{otherStudiesSameId.length} stud{otherStudiesSameId.length === 1 ? 'y' : 'ies'} found with the same patient ID: <b>{mainStudyPatientId}</b></span>
          : <span>No other studies found with the same patient ID</span>
        }
        {otherStudiesSameId && otherStudiesSameId.length > 0 && <>
          {dialogSelectedStudies.filter(study => study.patId === mainStudyPatientId).length === otherStudiesSameId.length
            ? <a onClick={() => {
                setDialogSelectedStudies(dialogSelectedStudies => dialogSelectedStudies.filter(study => study.patId !== mainStudyPatientId))
              }}>Deselect all</a>
            : <a onClick={() => {
                setDialogSelectedStudies(dialogSelectedStudies => [
                  ...dialogSelectedStudies,
                  ...otherStudiesSameId
                ].filter((v, i, a) => a.indexOf(v) === i))
            }}>Select all</a>
          }
        </>}
      </AddStudiesSubtitleDiv>
      
      {otherStudiesSameId && otherStudiesSameId.length > 0 && <ListArea>
        {otherStudiesSameId.map((item, i) => {
          const selected = dialogSelectedStudies.indexOf(item) >= 0
          return <StyledRow selected={selected} key={item.studyIuid}
            onClick={() => selected
              ? setDialogSelectedStudies(dialogSelectedStudies.filter(s => s.studyIuid !== item.studyIuid))
              : setDialogSelectedStudies([...dialogSelectedStudies, item])
            }> 
            <div className='info'>
              <div className="notranslate"><b>{item.modsInStudy ? item.modsInStudy + ' - ':''}{item.studyDesc && item.studyDesc.replace(/\^/g, ' ').trim()+' '}{item.studyDatetime ? '('+item.studyDatetime.slice(0,10)+')' : ''}</b></div>
              <div className='info-box'>
                {item.numInstances && <div> {item.numInstances} images {item.tagsStudy && item.tagsStudy.length > 0 && <>
                  {item.tagsStudy.sort((a, b) => a.tags.description > b.tags.description ? 1 : -1 )
                  .map((tag, i)  => <Tag className="notranslate" title={tag.tags.description} key={tag.tags.idTag} clickable={true}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setActiveFilteringTags(filteringTags => [...filteringTags, tag]
                      .filter((value, index, self) => self.findIndex(t => t.tags.idTag === value.tags.idTag) === index))
                  }}>{tag.tags.description}</Tag>)}
                </>}
                </div>}
              </div>
            </div>
            <SimpleCheckbox 
              raw
              key={item.studyIuid}
              value={dialogSelectedStudies.indexOf(item) >= 0}
              setValue={selected ? () => setDialogSelectedStudies(dialogSelectedStudies.filter(s => s.studyIuid !== item.studyIuid)) : () => setDialogSelectedStudies([...dialogSelectedStudies, item])}
            />
          </StyledRow>
        })}
      </ListArea>}

      {otherStudiesDifferentId && otherStudiesDifferentId.length > 0 &&
      <AddStudiesSubtitleDiv>
        <b>{otherStudiesDifferentId.length} {otherStudiesSameId.length > 0 && "more "}stud{otherStudiesDifferentId.length === 1 ? 'y' : 'ies'} found with the same patient name ({patientName}), DOB ({patientDob ? patientDob.slice(0,4)+'-'+patientDob.slice(4,6)+'-'+patientDob.slice(6,8) : 'null'}) and sex ({patientSex ? patientSex : 'null'})</b>
        {dialogSelectedStudies.filter(study => study.patId !== mainStudyPatientId).length === otherStudiesDifferentId.length
          ? <a style={{width: '108px', textAlign: 'right'}} onClick={() => {
              setDialogSelectedStudies(dialogSelectedStudies => dialogSelectedStudies.filter(study => study.patId === mainStudyPatientId))
            }}>Deselect all</a>
          : <a style={{width: '108px', textAlign: 'right'}} onClick={() => {
              setDialogSelectedStudies(dialogSelectedStudies => [
                ...dialogSelectedStudies,
                ...otherStudiesDifferentId
              ].filter((v, i, a) => a.indexOf(v) === i))
          }}>Select all</a>}
      </AddStudiesSubtitleDiv>}
      {otherStudiesDifferentId && otherStudiesDifferentId.length > 0 && <ListArea>
        {Object.entries(sortedGroupedItems).map(([patId, items], index) => (
          // {sortedGroupKeys.map(([patId, items], index) => (
          <div key={patId}>
              
              {/* SEPARATOR */}
              {index > 0 ? <PatientSeparator /> : <InvisibleSeparator />}
              <PatientIdParagraph><b>Patient ID: {patId}</b> ({items.length} stud{items.length === 1 ? 'y' : 'ies'})</PatientIdParagraph> {/* Print the patId */}
              {items.map((item, i) => {
              
                const selected = dialogSelectedStudies.indexOf(item) >= 0
                return <StyledRow selected={selected} key={item.studyIuid}
                  onClick={() => selected
                    ? setDialogSelectedStudies(dialogSelectedStudies.filter(s => s.studyIuid !== item.studyIuid))
                    : setDialogSelectedStudies([...dialogSelectedStudies, item])
                  }> 
                  
                  <div className='info'>
                    <div className="notranslate"><b>{item.modsInStudy ? item.modsInStudy + ' - ':''}{item.studyDesc && item.studyDesc.replace(/\^/g, ' ').trim()+' '}{item.studyDatetime ? '('+item.studyDatetime.slice(0,10)+')' : ''}</b></div>
                    <div className='info-box'>
                      {item.numInstances && <div> {item.numInstances} images {item.tagsStudy && item.tagsStudy.length > 0 && <>
                        {item.tagsStudy.sort((a, b) => a.tags.description > b.tags.description ? 1 : -1 )
                        .map((tag, i)  => <Tag className="notranslate" title={tag.tags.description} key={tag.tags.idTag} clickable={true}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setActiveFilteringTags(filteringTags => [...filteringTags, tag]
                            .filter((value, index, self) => self.findIndex(t => t.tags.idTag === value.tags.idTag) === index))
                        }}>{tag.tags.description}</Tag>)}
                      </>}
                      </div>}
                    </div>
                  </div>
                  <SimpleCheckbox 
                    raw
                    key={item.studyIuid}
                    value={dialogSelectedStudies.indexOf(item) >= 0}                    
                    setValue={selected ? () => setDialogSelectedStudies(dialogSelectedStudies.filter(s => s.studyIuid !== item.studyIuid)) : () => setDialogSelectedStudies([...dialogSelectedStudies, item])}
                  />
              </StyledRow>})}
          </div>
        ))}
      </ListArea>}
    </Dialog>}
  </>
}

export const CopyButton = ({url, highlight, success, icon, onCopy, label = (supported) => supported ? 'Copy link & Close' : 'Done'}) => {
  const ref = useRef()
  useEffect(() => {
    const container = ref.current.closest('.JS-copy-focus-container')
    const btn = new ClipboardJS('.copy-to-clipboard', container ? {container} : {})
    return () => btn.destroy()
  }, [])

  return <Button
    buttonRef={ref}
    className="copy-to-clipboard"
    data-clipboard-text={url}
    highlight={highlight} disabled={!url}
    success={success} icon={icon}
    onClick={() => callIfFunction(onCopy,ClipboardJS.isSupported())}
  >
    {callIfFunction(label,ClipboardJS.isSupported())}
  </Button>
}

const SecondStepDialog = ({closeDialog, closeWithNotification, confirmDialog, jwt, form, infoCard, caseRequest, labels, authorization, selectedStudies, setSelectedStudies}) => {

  const [message, setMessage] = useState('')
  const [subject, setSubject] = useState('A medical record has been shared with you')

  useEffect(() => {
    form.setValue('message', message)
    if(message.length >= 128){
      form.setError('message', 'Message: Maximum character limit of 128 reached.')
    }
  }, [message])

  useEffect(() => {
    form.setValue('subject', subject)
    if(subject.length >= 60){
      form.setError('subject', 'Subject: Maximum character limit of 60 reached.')
    }
  }, [subject])
  const availableStudiesCount = authorization && authorization.otherStudies && authorization.otherStudies.filter(study => study.studyIuid !== authorization.study.studyUID).length
  
  return <SharingDialog
    title={labels.secondStepTitle || (caseRequest ? "Share expert view" : "Confirm sharing record(s)")}
    closeDialog={closeDialog}
    form={form}
    footer={<>
      <Button onClick={confirmDialog.close}>Return</Button>
      <Button autoFocus highlight isLoading={form.isLoading} type="submit" onClick={form.willSubmitForm}>Confirm
        send</Button>
    </>
    }
    className="notranslate"  
  >
    <p>{labels.secondStepSubTitle}</p>
    <ExamIdentification>
      {infoCard}
    </ExamIdentification>
    {authorization && authorization.otherStudies && availableStudiesCount > 0 && selectedStudies.length > 0 && <OtherStudiesContainer 
      emptyList={selectedStudies.length === 0}
      >
      <AddOtherStudiesDiv>
        <div><b>Other studies from this patient</b> <span css="opacity:0.5;">({selectedStudies.length + "/" + availableStudiesCount} selected)</span></div>
      </AddOtherStudiesDiv>
      {selectedStudies.length > 0 && <OtherStudiesList>
        {selectedStudies.map((study, i) => 
          <OtherStudiesRow key={study.studyIuid}>
            <span css="margin-right:0.5rem;" className="notranslate">{study.modsInStudy ?  study.modsInStudy + ' - ' : ''}{study.studyDesc && study.studyDesc.replace(/\^/g, ' ').trim()}{study.studyDatetime && " ("+study.studyDatetime.slice(0,10)+")"} {study.numInstances && study.numInstances > 0 && ` - ${study.numInstances} images`}
              {study.tagsStudy.sort((a, b) => a.tags.description > b.tags.description ? 1 : -1 )
                .map((tag, i)  => <Tag className="notranslate" title={tag.tags.description} key={tag.tags.idTag}>{tag.tags.description}</Tag>)}
            </span>
          </OtherStudiesRow>)
        }  
      </OtherStudiesList>}
    </OtherStudiesContainer>}
    <EmailConfirmationListContainer>
      <p><b>{labels.sendingText || (caseRequest ? "Sending expert view to:" : "Sending record(s) to:")} </b></p>
      <ul className="sending-email-list notranslate">
        <FormValue path="emails">{emails =>
          emails.map(email => <li key={email}>{email}</li>)
        }</FormValue>
      </ul>
    </EmailConfirmationListContainer>
    <TextInput
      label="Subject"
      raw
      // path="subject"
      maxLength={60}
      value={subject}
      setValue={setSubject}
      // descText="This will be included in the email notification."
      // bellowDescText={
      //   <span style={{
      //     display: 'block',
      //     width: '100%',
      //     textAlign: 'right'
      //   }}>{subject.length+"/60"}</span>
      // }
    />
    <TextInput
      label="Message"
      raw
      // path="message"
      maxLength={128}
      value={message}
      setValue={setMessage}
      textarea
      autoResize
      descText="This will be included in the email notification."
      bellowDescText={
        <span style={{ 
          display: 'block',
          width: '100%',
          textAlign: 'right'
        }}>{message.length+"/128"}</span>
      }
    />
    <Errors/>
    <AdditionalText>{labels.additionalInfo}</AdditionalText>
    <SecurityDataWarning>
      <h4>This email may contain confidential and protected health care information.</h4>
      <p>Please be sure that the email of the recipient has been entered correctly
        and that you are using appropriately confidential mechanisms for this transmission.</p>
    </SecurityDataWarning>
  </SharingDialog>
}


const FlipDiv = ({useAnimation, onEnter = onEnter, onLeave = onLeave, ...props}) => {
  const ref = useRef()
  useAnimation(ref, onEnter, onLeave)
  return <div css="position:relative;" {...props} ref={ref}/>
}


const copyToClipboard = str => {
  const el = document.createElement('textarea')  // Create a <textarea> element
  el.value = str                                 // Set its value to the string that you want copied
  el.setAttribute('readonly', '')                // Make it readonly to be tamper-proof
  el.style.position = 'absolute'
  el.style.left = '-9999px'                      // Move outside the screen to make it invisible
  document.body.appendChild(el)                  // Append the <textarea> element to the HTML document
  const selected =
    document.getSelection().rangeCount > 0        // Check if there is any content selected previously
      ? document.getSelection().getRangeAt(0)     // Store selection if found
      : false                                    // Mark as false to know no selection existed before
  el.select()                                    // Select the <textarea> content
  document.execCommand('copy')                   // Copy - only works as a result of a user action (e.g. click events)
  document.body.removeChild(el)                  // Remove the <textarea> element
  if (selected) {                                 // If a selection existed before copying
    document.getSelection().removeAllRanges()    // Unselect everything on the HTML document
    document.getSelection().addRange(selected)   // Restore the original selection
  }
}

const ShareableLinkInput = styled.div`
  overflow: hidden;
  position: relative;
  padding: 2px;
  margin: -2px;
  ${Loading} {
    bottom: 11px;
    left: 9px;
    position: absolute;
  }
  ${Icon} {
    font-size: 23px;
    position: absolute;
    bottom: 9px;
    left: 10px;
    color: rgba(0,0,0,0.7);
  }
  input {
    overflow:hidden;
    background: rgba(0,0,0,0.05);
    padding-left: 2.75rem;
    font-size: 12px;
    display: block;
    word-break: normal;
  }
  > div {
    margin-bottom: 0;
  }
`

export const CopyLinkComponent = ({url, label, loadingShareableLink}) => {
  const ref = useRef()
  useEffect(() => {
    if (url) {
      ref.current.select()
    }
  }, [])
  return <ShareableLinkInput>
    {loadingShareableLink ? <Loading size={17} borderWidth={3}/> : <Icon icon="link"/>}
    <TextInput readOnly label={label} raw ref={ref} onClick={(e) => e.target.select()}
               disabled={loadingShareableLink}
               value={loadingShareableLink ? 'Generating shareable link...' : (_.isString(url) ? url : '')}/>
  </ShareableLinkInput>
}

const ExpirationSelectButton = styled.div`
  background-color: rgba(0,0,0,0.06);
  border-radius: 6px;
  padding: 0.5rem 2.75rem 0.5rem 1rem;
  min-height: 36px;
  margin-bottom: 1.5rem;
  ${props => props.isCustomExpiry && `margin-bottom: 0.5rem;`}
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: -0.5rem;
  position: relative;
  :hover {
    background-color: rgba(0,0,0,0.08);
  }
  :active {
    background-color: rgba(0,0,0,0.1);
  }
  .help-text {
    font-weight: 400;
    color: ${getColor('gray120')};
    font-size: 12px;
  }
  ${Icon} {
    font-size: 12px;
    color: rgba(0,0,0,0.5);
    right: 1rem;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
`



const ExpirationSelect = ({onChange}) => {
  const menu = usePopupToggle()
  const [expiration, setExpiration] = useFormValue('expirationTime')
  const [expirationDate, setExpirationDate] = useFormValue('expirationDate')
  const now = new Date();
  const timezoneOffsetMinutes = now.getTimezoneOffset();
  const timezoneOffsetHours = -timezoneOffsetMinutes / 60;
  const DATE_FORMAT = "yyyy-MM-dd"
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  const isLocalTimePast23 = now.getHours() >= 23
  const minDate = isLocalTimePast23 ? now : yesterday

  useEffect(() => {
    if((expiration > -1) || (expiration === -1 && expirationDate && expirationDate > minDate)) {
      onChange()
    }
  }, [expiration, expirationDate])

  const currentExpiration = () => <ExpirationSelectButton onClick={menu.toggle} isCustomExpiry={expiration === -1}>
    <div><b>Expiration time:</b> <span className="help-text">{EXPIRATION_LABELS[expiration]}</span></div>
    <Icon icon="arrow-down"/>
    {
      menu.isOpen &&
      <ContextMenu fill>
        {EXPIRATION_TIMES.map(time =>
          <Li icon={time === expiration && 'check'} label={EXPIRATION_LABELS[time]} key={time + ''}
              onClick={() => setExpiration(time)}/>
        )}
      </ContextMenu>
    }
  </ExpirationSelectButton>

  return expiration === -1 ? <TransitionDiv>
    {currentExpiration()}
    <DatePicker placeholder={'YYYY-MM-DD'} label="Expiration Date" min={minDate} path="expirationDate" mandatory format={DATE_FORMAT} timezoneOffset={timezoneOffsetHours} />
  </TransitionDiv> : currentExpiration()

}